import { ILogService, ITimeoutService, IWindowService } from "angular";
import { StateService, TransitionService } from "angular-ui-router";
import { AbortedXhrError } from "../../core/interceptors/apex-api-response.interceptor";
import { isChunkLoadError } from "../../core/router/app-update-transitions";
import { HardReloadService } from "../../core/router/hard-reload.service";
import { REJECT_TYPE } from "../../core/router/transition-errors";
import { UtilityService } from "../../core/services/utility.service";


export function attachLoadingTransitions($state: StateService, $transitions: TransitionService,
    $log: ILogService, $window: IWindowService, utilityService: UtilityService) {
    'ngInject';
    $state.defaultErrorHandler(({ type, detail, message }) => {
        // Rejected errors are handled in the onError handler, so only
        // need to log other types of errors to the console.
        if (type === REJECT_TYPE.SUPERSEDED) {
            // These are rarely actual errors.
            $log.info(message, { type, detail });
        } else if (type !== REJECT_TYPE.ERROR) {
            $log.error(message, { type, detail });
        }
    });

    $transitions.onCreate({}, function (transition) {
        // Show the loading spinner even before checking that transitioning is possible.
        utilityService.showLoading();
    });

    $transitions.onSuccess({}, function (transition) {
        utilityService.hideLoading();

        // Update the page header on successful navigation.
        const to = transition.$to();
        if (to.data && (to.data.title || to.data.pageHeader)) {
            document.title = to.data.title || to.data.pageHeader;
        }
    });

    $transitions.onError({}, function (transition) {
        utilityService.hideLoading();

        const from = transition.$from();
        const { type, detail, message } = transition.error();
        if (from.data && (from.data.title || from.data.pageHeader)) {
            document.title = from.data.title || from.data.pageHeader;
        }

        if (type === REJECT_TYPE.ERROR && !isChunkLoadError(detail)) {
            if (detail && (detail.xhrStatus === "abort" || detail instanceof AbortedXhrError)) {
                // The browser aborted the transition. Normal when leaving the page, so ignore it completely.
                return;
            }
            // 403 and 404 are user-driven errors, so don't log them as warnings or errors.
            if (detail && detail.status === 404) {
                $log.info(message, detail, transition.to());
                $state.go("persisted404");
                return;
            }
            if (detail && detail.status === 403) {
                // User was denied access to the page.
                $log.info(message, detail, transition.to());
                $state.go("persisted403");
                return;
            }
            if (detail && detail.status === 503) {
                // Show a specific error page while the API site is unavailable.
                $log.error(message, detail, transition.to());
                $state.go("persisted503");
                return;
            }

            // For all other errors, show a generic navigation error page.
            $log.error(message, detail, transition.to());
            $state.go("navigationFailed", { message: message, state: transition.$to(), params: transition.params() });
        }
    });

    $transitions.onEnter({}, function (transition) {
        const to = transition.$to();
        if (to.data && (to.data.title || to.data.pageHeader)) {
            document.title = to.data.title || to.data.pageHeader;
        }
    });

    $transitions.onStart({}, function (transition) {
        if ($window.onbeforeunload) {
            try {
                const unloadTest = $window.onbeforeunload(null);
                if (unloadTest && !confirm(unloadTest)) {
                    // Cancel the transition.
                    return false;
                }
            } catch (ex) {
                // The exception is irrelevant. In most cases, if not cleaned up, the exception
                // would be due to calling methods within an already cleaned up component.
            }
            // Ensure the onbeforeunload handler doesn't stick between transitions.
            $window.onbeforeunload = () => { };
        }
    })
}

/**
 * Attach state change events exclusive to the main project
 * @param {any} $transitions
 * @param {Window} $window The window facade.
 */
export function attachStateChangeEvents($transitions: TransitionService, $timeout: ITimeoutService,
    $window: IWindowService, hardReloadService: HardReloadService) {
    'ngInject'

    $transitions.onSuccess({}, function onTransitionSuccess(transition) {
        if (hardReloadService.shouldReload(transition)) {
            console.log("Reloading the page to trigger transition to server-rendered route");
            return Promise.resolve($timeout(() => {
                hardReloadService.reload();
            })
                // Delay resolution of the route after reloading the window.
                // This avoids some problems where reload() just sends the browser back to the current page.
                .then(() => $timeout(() => { }, 1000)));
        }
    });
}
